// @ts-nocheck
// Agent Portal (/cti2) — Dialer's collapsed icon rail, split out of
// Dialer.tsx. Pure presentation; all state/derivations live in the parent
// (workState, dialerControls, mainAction etc are already fully computed by
// the time they reach here).
import React from 'react';
import { FiLoader, FiPhoneCall, FiPhoneOff, FiVoicemail } from 'react-icons/fi';
import { PanelLeftOpen } from 'lucide-react';
import { motion } from 'framer-motion';
import { formatWorkDuration } from '../utils/dialerFormat';

export default function DialerCollapsedRail({
  onExpand,
  workState,
  screen,
  isMobileLogin,
  outgoingRingingActive,
  onAccept,
  isAnswering = false,
  onVoicemail,
  voicemailSending = false,
  voicemailReady = true,
  dialerControls,
  mainAction,
  callBlocked,
  callMuted = false,
  callTitle,
  isOnCall,
  localHangupRequested,
  settingsSlot = null,
}) {
  return (
    <motion.div
      key="collapsed"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
      className="flex h-full min-h-0 flex-col items-center gap-2"
    >
      <button
        type="button"
        onClick={onExpand}
        title="Expand dialer"
        aria-label="Expand dialer"
        className="flex h-7 w-7 items-center justify-center rounded-2xl bg-card text-muted-foreground shadow-neu-raised transition-colors hover:text-foreground"
      >
        <PanelLeftOpen size={16} />
      </button>
      <div className="h-px w-7 flex-shrink-0 bg-border" />
      <div
        className="flex flex-col items-center gap-0.5 rounded-2xl bg-card px-1.5 py-1.5 shadow-neu-raised"
        title={`${workState.label}${workState.seconds != null ? ` · ${formatWorkDuration(workState.seconds)}` : ''}`}
      >
        <span
          className={`h-2.5 w-2.5 rounded-full ${
            { ring: 'bg-destructive animate-pulse', live: 'bg-success', hold: 'bg-warning', wrap: 'bg-orange-500' }[workState.evt] || 'bg-blue-500'
          }`}
        />
        {workState.seconds != null && (
          <span className="font-mono text-[9px] leading-none text-foreground">
            {formatWorkDuration(workState.seconds)}
          </span>
        )}
      </div>
      {screen === 'ringing' && !isMobileLogin && !outgoingRingingActive && (
        <button
          id="call"
          type="button"
          onClick={onAccept}
          disabled={isAnswering}
          title={isAnswering ? 'Answering…' : 'Answer'}
          className="flex h-9 w-9 items-center justify-center rounded-2xl bg-success text-success-foreground shadow-neu-raised disabled:cursor-not-allowed disabled:opacity-60"
        >
          {isAnswering ? <FiLoader size={14} className="animate-spin" /> : <FiPhoneCall size={14} />}
        </button>
      )}
      {screen === 'ringing' && onVoicemail && (
        <button
          type="button"
          onClick={onVoicemail}
          disabled={voicemailSending || !voicemailReady}
          title={voicemailReady ? 'Send this call to voicemail' : 'Waiting for call to connect…'}
          className="flex h-9 w-9 items-center justify-center rounded-2xl bg-muted text-muted-foreground shadow-neu-raised transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
        >
          <FiVoicemail size={14} />
        </button>
      )}
      {dialerControls.map((c) => (
        <button
          key={c.key}
          type="button"
          title={c.label}
          disabled={c.disabled}
          aria-pressed={c.active}
          onClick={c.onClick}
          className={`flex h-9 w-9 items-center justify-center rounded-[10px] border transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${c.active ? 'border-primary/40 bg-primary/15 text-primary' : 'border-border bg-muted text-muted-foreground hover:border-foreground/30 hover:text-foreground'}`}
        >
          {c.icon}
        </button>
      ))}
      {/* Dialer settings gear (only when Auto Answer is enabled for the agent). */}
      {settingsSlot}
      <div className="mt-auto flex flex-shrink-0 flex-col items-center gap-2">
        <button
          type="button"
          onClick={mainAction}
          disabled={(isOnCall && !mainAction) || callBlocked}
          id={isOnCall ? 'hangupButtonOnIdleScreen' : 'callButtonOnIdleScreen'}
          title={isOnCall ? (localHangupRequested ? 'Ending…' : 'Hang up') : callMuted ? callTitle : callBlocked ? 'Activate WebRTC or Mobile profile to call' : 'Call'}
          className={`flex h-9 w-9 items-center justify-center rounded-2xl shadow-neu-raised disabled:cursor-not-allowed disabled:opacity-50 ${isOnCall ? 'bg-destructive text-destructive-foreground' : callBlocked || callMuted ? `bg-muted text-muted-foreground shadow-none${callMuted ? ' cursor-not-allowed' : ''}` : 'bg-success text-success-foreground'}`}
        >
          {isOnCall ? <FiPhoneOff size={14} /> : <FiPhoneCall size={14} />}
        </button>
      </div>
    </motion.div>
  );
}
